import React from "react";
import { Helmet } from "react-helmet-async";

/**
 * SEO component for per-page metadata.
 *
 * Usage:
 *   <Seo title="Departments" description="Browse our specialties" path="/departments" />
 *
 * Renders standard <title>, description, canonical, OpenGraph & Twitter cards.
 * Defaults are tuned for Maroof International Hospital (Islamabad, PK).
 */

const SITE_URL = "https://maroof.com.pk";
const SITE_NAME = "Maroof International Hospital";

export { SITE_URL, SITE_NAME };
const DEFAULT_DESC =
  "Maroof International Hospital — multi-disciplinary tertiary care hospital in F-10 Markaz, Islamabad. Book appointments, view doctor profiles, departments, lab reports & more.";
const DEFAULT_IMG = `${SITE_URL}/images/logo/logo-full.png`;

const Seo = ({ title, description = DEFAULT_DESC, path = "", image = DEFAULT_IMG, type = "website", keywords, jsonLd, noindex = false }) => {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
  const url = `${SITE_URL}${path}`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
    </Helmet>
  );
};

export const HospitalSchema = {
  "@context": "https://schema.org",
  "@type": "Hospital",
  name: SITE_NAME,
  alternateName: "MIH",
  url: SITE_URL,
  logo: DEFAULT_IMG,
  image: DEFAULT_IMG,
  telephone: "+92-51-111-644-911",
  email: "info@maroof.com.pk",
  medicalSpecialty: [
    "Cardiology",
    "Orthopedics",
    "Gynecology",
    "Pediatrics",
    "Neurology",
    "Gastroenterology",
    "Dermatology",
    "General Surgery",
    "Internal Medicine",
    "ENT",
  ],
  address: {
    "@type": "PostalAddress",
    streetAddress: "F-10 Markaz",
    addressLocality: "Islamabad",
    addressRegion: "ICT",
    postalCode: "44000",
    addressCountry: "PK",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 33.6974597,
    longitude: 73.0116512,
  },
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      opens: "00:00",
      closes: "23:59",
      description: "24/7 Emergency Services",
    },
  ],
  sameAs: ["https://www.facebook.com/MaroofInternationalHospital"],
};

export default Seo;
